/**
 * TC credit card detail (UI v3 layout, BE-4 data)
 *
 * Route: /accounting/credit-cards/:id (?pay=1 opens Make Payment)
 * Ledger history with running balance, bill payments (post immediately, no approval), Reverse with a reason,
 * and a receipt per bill payment.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Grid, Alert, Snackbar, IconButton, Tooltip, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, InputAdornment, CircularProgress, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, CreditCard as CreditCardIcon, Payment as PaymentIcon, Warning as WarningIcon,
  Undo as ReverseIcon, AttachFile as ReceiptIcon,
} from '@mui/icons-material';
import { StatusChip, ReceiptAttachment, JournalEntryLink } from '../../components/accounting';
import { formatCurrency, formatDate } from '../../utils/accounting/formatters';
import { fontSize, fontWeight } from '../../utils/accounting/styles';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { can } from '../../utils/roleUtils';
import { creditCardService } from '../../services/accounting/creditCard.service';
import { serverMessage } from '../../utils/apiConvert';
import { cardLabel, ordinal, daysUntilDue } from './CreditCards';

const today = () => new Date().toISOString().slice(0, 10);
const twoDecimals = (v) => /^\d+(\.\d{1,2})?$/.test(String(v));

const sessionUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
};

const SOURCE_LABEL = {
  CC_PAYMENT: 'Bill payment',
  CC_CHARGE: 'Card charge',
  REVERSAL: 'Reversal',
  MANUAL: 'Opening balance',
  OPENING_BALANCE: 'Opening balance',
  EXPENSE: 'Expense',
  GOVT_FEE: 'Government fee',
  WORK_ORDER_ADDON: 'Work order add-on',
  FINE: 'Fine',
  PARTNER_PAYMENT: 'Partner payment',
  COMMISSION_PAYMENT: 'Commission payment',
  SUBSCRIPTION_PAYMENT: 'Subscription',
};

const headSx = { fontSize: fontSize.xs, fontWeight: fontWeight.semibold };
const cellSx = { fontSize: '13px', py: 1 };

const CreditCardDetail = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const canWrite = can(sessionUser(), 'finance', 'write');
  const [card, setCard] = useState(null);
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState({ from: '', to: '' });
  const [loading, setLoading] = useState(true);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ sourceAccountId: '', amount: '', paidAt: today(), reference: '', file: null });
  const [payError, setPayError] = useState(null);
  const [reverseRow, setReverseRow] = useState(null);
  const [reason, setReason] = useState('');
  const [receiptRow, setReceiptRow] = useState(null);
  const [receiptInfo, setReceiptInfo] = useState({ loading: false, hasReceipt: false, filename: '' });
  const [busy, setBusy] = useState(false);

  const loadCard = useCallback(async () => {
    try {
      const all = await creditCardService.getCards();
      setCard(all.find((c) => c.id === id) || null);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load the card'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadRows = useCallback(async () => {
    setRowsLoading(true);
    try {
      setRows(await creditCardService.getTransactions(id, range));
    } catch (err) {
      setError(serverMessage(err, 'Failed to load the card history'));
    } finally {
      setRowsLoading(false);
    }
  }, [id, range]);

  useEffect(() => { loadCard(); }, [loadCard]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const refresh = async () => { await Promise.all([loadCard(), loadRows()]); };

  const openPay = useCallback(() => {
    setPay({ sourceAccountId: '', amount: card?.outstanding > 0 ? Number(card.outstanding).toFixed(2) : '', paidAt: today(), reference: '', file: null });
    setPayError(null);
    setPayOpen(true);
    if (accounts.length === 0) creditCardService.getPayingAccounts().then(setAccounts).catch(() => {});
  }, [card, accounts.length]);

  useEffect(() => {
    if (card && searchParams.get('pay') === '1' && canWrite) {
      openPay();
      setSearchParams({}, { replace: true });
    }
  }, [card, searchParams, canWrite, openPay, setSearchParams]);

  const submitPay = async () => {
    setBusy(true);
    setPayError(null);
    try {
      const payment = await creditCardService.payBill(id, {
        sourceAccountId: pay.sourceAccountId, amount: pay.amount, paidAt: pay.paidAt, reference: pay.reference,
      });
      if (pay.file && payment?.id) {
        try {
          await creditCardService.attachReceipt(id, payment.id, pay.file);
        } catch (err) {
          setError(`Payment recorded, but the receipt was not saved: ${serverMessage(err, 'upload failed')}`);
        }
      }
      setPayOpen(false);
      setSnackbar('Payment recorded');
      await refresh();
    } catch (err) {
      setPayError(serverMessage(err, 'Failed to record the payment'));
    } finally {
      setBusy(false);
    }
  };

  const submitReverse = async () => {
    setBusy(true);
    setError(null);
    try {
      await creditCardService.reversePayment(id, reverseRow.sourceId, reason.trim());
      setReverseRow(null);
      setSnackbar('Payment reversed — the card is owed again');
      await refresh();
    } catch (err) {
      setReverseRow(null);
      setError(serverMessage(err, 'Failed to reverse the payment'));
    } finally {
      setBusy(false);
    }
  };

  const openReceipt = async (row) => {
    setReceiptRow(row);
    setReceiptInfo({ loading: true, hasReceipt: false, filename: '' });
    try {
      const r = await creditCardService.receiptUrl(id, row.sourceId);
      setReceiptInfo({ loading: false, hasReceipt: true, filename: r?.filename || '' });
    } catch (err) {
      setReceiptInfo({ loading: false, hasReceipt: false, filename: '' });
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" py={8}><CircularProgress /></Box>;
  }
  if (!card) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error || 'Credit card not found'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/accounting/credit-cards')} sx={{ mt: 2 }}>Back to Credit Cards</Button>
      </Box>
    );
  }

  const outstanding = Number(card.outstanding || 0);
  const daysLeft = daysUntilDue(card);
  const isDueSoon = outstanding > 0 && daysLeft != null && daysLeft <= 7;
  const selectedAccount = accounts.find((a) => a.id === pay.sourceAccountId);
  const payValid = pay.sourceAccountId && Number(pay.amount) > 0 && twoDecimals(pay.amount) && pay.paidAt && pay.paidAt <= today();
  const history = [...rows].reverse();

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {/* Back + Header */}
      <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
        <IconButton size="small" onClick={() => navigate('/accounting/credit-cards')}><ArrowBackIcon sx={{ fontSize: 20 }} /></IconButton>
        <CreditCardIcon sx={{ fontSize: 22, color: 'secondary.main' }} />
        <Typography variant="h5" fontWeight={600} color="secondary.main">{cardLabel(card)}</Typography>
        <Chip label={card.bankIssuer} size="small" sx={{ fontSize: fontSize.xs, height: 22, ml: 0.5 }} />
        {card.active === false && <Chip label="Inactive" size="small" sx={{ fontSize: fontSize.xs, height: 22 }} />}
        {isDueSoon && (
          <Chip icon={<WarningIcon sx={{ fontSize: 14 }} />} label={daysLeft === 0 ? 'Due today' : `Due in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`}
            size="small" color="warning" sx={{ fontSize: fontSize.xs, height: 22 }} />
        )}
      </Box>
      <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 1.5, ml: 0.5 }}>
        {[card.cardName, card.cardholderName].filter(Boolean).join(' · ')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Card Summary + Make Payment */}
      <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
          <Grid container spacing={2} sx={{ flex: 1, minWidth: 260 }}>
            <Grid item xs={6} sm={3}>
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Outstanding Balance</Typography>
              <Typography sx={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: outstanding > 0 ? 'error.main' : 'success.main' }}>
                {formatCurrency(outstanding)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>This Month Charges</Typography>
              <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold }}>{formatCurrency(card.thisMonthCharges)}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Payment Due Day</Typography>
              <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold }}>
                {card.paymentDueDay ? `${ordinal(Number(card.paymentDueDay))} of each month` : '—'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Last Payment</Typography>
              <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold }}>
                {card.lastPaymentDate ? `${formatDate(card.lastPaymentDate)} — ${formatCurrency(card.lastPaymentAmount)}` : '—'}
              </Typography>
            </Grid>
          </Grid>
          {canWrite && (
            <Button variant="contained" size="small" startIcon={<PaymentIcon sx={{ fontSize: 16 }} />} onClick={openPay}
              sx={{ fontSize: fontSize.sm, whiteSpace: 'nowrap', textTransform: 'none' }}>
              Make Payment
            </Button>
          )}
        </Box>
      </Paper>

      {/* Transaction History */}
      <Paper variant="outlined">
        <Box sx={{ p: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold }}>Transaction History ({history.length})</Typography>
          <Box display="flex" gap={1}>
            <TextField type="date" size="small" label="From" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
            <TextField type="date" size="small" label="To" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
          </Box>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow>
                <TableCell sx={headSx}>Date</TableCell>
                <TableCell sx={{ ...headSx, minWidth: 220 }}>Description</TableCell>
                <TableCell align="right" sx={headSx}>Charge (+)</TableCell>
                <TableCell align="right" sx={headSx}>Payment (-)</TableCell>
                <TableCell align="right" sx={headSx}>Balance</TableCell>
                <TableCell align="right" sx={{ ...headSx, width: 90 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsLoading ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>No transactions found</Typography>
                  </TableCell>
                </TableRow>
              ) : history.map((r) => {
                const isPayment = r.sourceType === 'CC_PAYMENT';
                return (
                  <TableRow key={`${r.journalEntryId}-${r.balance}`} hover>
                    <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>{formatDate(r.entryDate)}</TableCell>
                    <TableCell sx={cellSx}>
                      {r.description}
                      <Box display="flex" gap={0.5} mt={0.25} alignItems="center">
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{SOURCE_LABEL[r.sourceType] || r.sourceType}</Typography>
                        {r.reversed && <StatusChip tone="default" label="Reversed" />}
                        <JournalEntryLink id={r.journalEntryId} />
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      {Number(r.charge) > 0 ? (
                        <Typography sx={{ fontSize: '13px', fontWeight: fontWeight.semibold, color: isDark ? '#ef5350' : '#d32f2f' }}>
                          +{formatCurrency(r.charge, { withPrefix: false })}
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      {Number(r.payment) > 0 ? (
                        <Typography sx={{ fontSize: '13px', fontWeight: fontWeight.semibold, color: isDark ? '#66bb6a' : '#2e7d32' }}>
                          -{formatCurrency(r.payment, { withPrefix: false })}
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      <Typography sx={{ fontSize: '13px', fontWeight: fontWeight.medium }}>{formatCurrency(r.balance)}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      {isPayment && (
                        <Box display="flex" justifyContent="flex-end" gap={0.5}>
                          <Tooltip title="Receipt"><IconButton size="small" onClick={() => openReceipt(r)}><ReceiptIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                          {canWrite && !r.reversed && (
                            <Tooltip title="Reverse payment">
                              <IconButton size="small" color="error" onClick={() => { setReason(''); setReverseRow(r); }}><ReverseIcon sx={{ fontSize: 18 }} /></IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Payment Dialog ── */}
      <Dialog open={payOpen} onClose={() => !busy && setPayOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, pb: 1 }}>Pay {cardLabel(card)}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 1.5, p: 1.5, bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f5f5f5', borderRadius: 1 }}>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Outstanding Balance</Typography>
            <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: 'error.main' }}>{formatCurrency(card.outstanding)}</Typography>
          </Box>
          {payError && <Alert severity="error" sx={{ mb: 1.5, fontSize: fontSize.sm }}>{payError}</Alert>}
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Amount" type="number" required value={pay.amount}
                onChange={(e) => setPay((p) => ({ ...p, amount: e.target.value }))} error={pay.amount !== '' && !twoDecimals(pay.amount)}
                InputProps={{ endAdornment: <InputAdornment position="end">AED</InputAdornment>, inputProps: { min: 0, step: '0.01' } }} />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Pay From" required value={pay.sourceAccountId}
                onChange={(e) => setPay((p) => ({ ...p, sourceAccountId: e.target.value }))}
                helperText={accounts.length === 0 ? 'Needs an active operations bank or cash account'
                  : selectedAccount && Number(pay.amount) > Number(selectedAccount.balance || 0)
                    ? `This will make ${selectedAccount.label} negative (balance ${formatCurrency(selectedAccount.balance)})` : ' '}
                FormHelperTextProps={{ sx: { color: selectedAccount && Number(pay.amount) > Number(selectedAccount.balance || 0) ? 'warning.main' : undefined } }}>
                {accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.label}{a.type === 'CASH' ? ' (cash)' : ''}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Date" type="date" required value={pay.paidAt}
                onChange={(e) => setPay((p) => ({ ...p, paidAt: e.target.value }))} InputLabelProps={{ shrink: true }} inputProps={{ max: today() }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Reference" value={pay.reference} placeholder="e.g. bank transfer ref"
                onChange={(e) => setPay((p) => ({ ...p, reference: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <Button component="label" size="small" variant="outlined" startIcon={<ReceiptIcon />} sx={{ fontSize: fontSize.xs, textTransform: 'none' }}>
                {pay.file ? pay.file.name : 'Upload Receipt'}
                <input type="file" hidden accept="image/*,application/pdf" onChange={(e) => setPay((p) => ({ ...p, file: e.target.files?.[0] || null }))} />
              </Button>
            </Grid>
          </Grid>
          <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', mt: 1 }}>Posts immediately. A wrong payment can be reversed later.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPayOpen(false)} disabled={busy} size="small" sx={{ fontSize: fontSize.sm, textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" size="small" onClick={submitPay} disabled={busy || !payValid} sx={{ fontSize: fontSize.sm, textTransform: 'none' }}>
            {busy ? <CircularProgress size={18} color="inherit" /> : 'Submit Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reverse */}
      <Dialog open={!!reverseRow} onClose={() => setReverseRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reverse Bill Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {reverseRow && `${formatCurrency(reverseRow.payment)} paid on ${formatDate(reverseRow.entryDate)}. The card is owed again and the money returns to the account it was paid from.`}
          </Typography>
          <TextField label="Reason *" value={reason} onChange={(e) => setReason(e.target.value)} fullWidth multiline rows={3} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReverseRow(null)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={busy || !reason.trim()} onClick={submitReverse}>Reverse</Button>
        </DialogActions>
      </Dialog>

      {/* Receipt */}
      <Dialog open={!!receiptRow} onClose={() => setReceiptRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Payment Receipt</DialogTitle>
        <DialogContent>
          {receiptRow && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {formatCurrency(receiptRow.payment)} paid on {formatDate(receiptRow.entryDate)}
            </Typography>
          )}
          {receiptInfo.loading ? <CircularProgress size={20} /> : receiptRow && (
            <ReceiptAttachment
              hasReceipt={receiptInfo.hasReceipt}
              filename={receiptInfo.filename}
              canEdit={canWrite}
              fetchUrl={() => creditCardService.receiptUrl(id, receiptRow.sourceId)}
              upload={(file) => creditCardService.attachReceipt(id, receiptRow.sourceId, file)}
              onUploaded={() => { setSnackbar('Receipt saved'); openReceipt(receiptRow); }}
              onError={setError}
            />
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setReceiptRow(null)}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar(null)} message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default CreditCardDetail;
